import React, { useRef, useEffect, useState } from 'react';
import Slider from 'react-slick';
import { housesData } from '../data';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import '../styles/Services.css';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { motion } from 'framer-motion';

gsap.registerPlugin(ScrollTrigger);

export const Services = ({ isMobileMenuOpen }) => {
  const sliderRef = useRef(null);
  const slickSliderRef = useRef(null);
  const prevButtonRef = useRef(null);
  const nextButtonRef = useRef(null);
  const prevIconRef = useRef(null);
  const nextIconRef = useRef(null);
  const prevSvgRef = useRef(null);
  const nextSvgRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isHoveredPrev, setIsHoveredPrev] = useState(false);
  const [isHoveredNext, setIsHoveredNext] = useState(false);
  const [sliderVisible, setSliderVisible] = useState(false);

  // Use only 8 houses for the slider (limit to 8 items)
  const sliderHouses = housesData.slice(0, 8);

  // Updated settings for slider to match the reference design
  const settings = {
    infinite: true,
    speed: 500,
    slidesToShow: 3, // Show 3 images by default
    slidesToScroll: 1,
    centerMode: false,
    arrows: false,
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          centerMode: true,
          centerPadding: '40px',
        },
      },
    ],
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;
      const targetPosition = windowHeight * 0.7;

      if (scrollPosition >= targetPosition) {
        setIsVisible(true);
        window.removeEventListener('scroll', handleScroll);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (isVisible) {
      gsap.to(sliderRef.current, {
        y: 0,
        opacity: 1,
        duration: 1,
        ease: 'power3.out',
        onComplete: () => {
          setSliderVisible(true);
          if (prevButtonRef.current && nextButtonRef.current && prevSvgRef.current && nextSvgRef.current) {
            gsap.to(prevSvgRef.current, { opacity: 1, duration: 0.1 });
            gsap.to(nextSvgRef.current, { opacity: 1, duration: 0.1 });
            gsap.fromTo(".prev-button-border", { strokeDasharray: "0 100" }, { strokeDasharray: "100 0", duration: 1.2, ease: "power2.out", delay: 0.2 });
            gsap.fromTo(".next-button-border", { strokeDasharray: "0 100" }, { strokeDasharray: "100 0", duration: 1.2, ease: "power2.out", delay: 0.4 });
            gsap.fromTo(prevIconRef.current, { opacity: 0, scale: 0.7 }, { opacity: 1, scale: 1, duration: 0.3, ease: "back.out(1.7)", delay: 0.2 + (1.2 * 0.95) });
            gsap.fromTo(nextIconRef.current, { opacity: 0, scale: 0.7 }, { opacity: 1, scale: 1, duration: 0.3, ease: "back.out(1.7)", delay: 0.4 + (1.2 * 0.95) });
          }
        }
      });
      ScrollTrigger.refresh();
    }
  }, [isVisible]);

  const nextSlide = () => {
    if (slickSliderRef.current) {
      slickSliderRef.current.slickNext();
    }
  };

  const prevSlide = () => {
    if (slickSliderRef.current) {
      slickSliderRef.current.slickPrev();
    }
  };

  const textAnimation = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.6 } },
  };

  const text = [
    'We focus on the acquisition of properties and land for the purpose of high-quality real estate developments.',
    'Our proven expertise in this area is evidenced by our track record of success in acquiring properties by putting',
    "sellers' needs and interests first. We understand the importance of clear and fair negotiations and always strive",
    'to maintain transparency throughout the transaction process to ensure satisfaction for all parties involved.',
  ].join(' ');

  useEffect(() => {
    if (isHoveredPrev && sliderVisible) {
      gsap.set(".prev-button-border", { strokeDasharray: "100 0" });
      gsap.to(".prev-button-border", { strokeDasharray: "0 100", duration: 0.6, ease: "power2.in" });
    } else if (!isHoveredPrev && sliderVisible && prevButtonRef.current) {
      gsap.set(".prev-button-border", { strokeDasharray: "0 100" });
      gsap.to(".prev-button-border", { strokeDasharray: "100 0", duration: 0.6, ease: "power2.out" });
    }
  }, [isHoveredPrev, sliderVisible]);

  useEffect(() => {
    if (isHoveredNext && sliderVisible) {
      gsap.set(".next-button-border", { strokeDasharray: "100 0" });
      gsap.to(".next-button-border", { strokeDasharray: "0 100", duration: 0.6, ease: "power2.in" });
    } else if (!isHoveredNext && sliderVisible && nextButtonRef.current) {
      gsap.set(".next-button-border", { strokeDasharray: "0 100" });
      gsap.to(".next-button-border", { strokeDasharray: "100 0", duration: 0.6, ease: "power2.out" });
      gsap.to(nextIconRef.current, { opacity: 1, scale: 1, duration: 0.3, ease: "back.out(1.7)" });
    }
  }, [isHoveredNext, sliderVisible]);

  return (
    <section id="services" className="h-auto">
      <div
        className={`serv_h flex flex-col mb-6 justify-start items-start ml-[2.5rem] md:ml-[2.5rem] 
          text-white xl:max-w-[930px] lg:max-w-[930px] md:max-w-[730px] sm:max-w-[130px] my-auto ${
            isMobileMenuOpen ? 'services-mobile-height' : ''
          }`}
      >
        <h1 className="services-heading inline-block leading-[2.5rem] text-[3.5rem] font-normal tracking-tight">
        Acquisition
        </h1>
        <div className="s-t py-2 w-11/12">
          <motion.p
            initial="hidden"
            animate={isVisible ? 'visible' : 'hidden'}
            variants={textAnimation}
            className="services-text txt xl:p-0 lg:p-0 md:p-0 sm:p-2 p-2 text-left"
          >
            {text}
          </motion.p>
        </div>
      </div>
      
      {/* Navigation buttons positioned at top right before the slider */}
      <div className="slider-navigation flex justify-end px-6 py-4 -mt-16 mr-[0.2rem]">
        <div ref={prevButtonRef} onClick={prevSlide} onMouseEnter={() => setIsHoveredPrev(true)} onMouseLeave={() => setIsHoveredPrev(false)} style={{ position: 'relative', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'transparent', borderRadius: '50%', cursor: 'pointer', margin: '0 5px', zIndex: 10 }}>
          <svg ref={prevSvgRef} width="40" height="40" viewBox="0 0 40 40" style={{ position: 'absolute', top: 0, left: 0, opacity: 0 }}>
            <circle cx="20" cy="20" r="18" fill="transparent" stroke="rgba(39, 68, 236, 0.767)" strokeWidth="1" className="prev-button-border" />
          </svg>
          <div ref={prevIconRef} style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 2, opacity: 0 }}>
            <svg width="20" height="20" viewBox="0 0 20 20">
              <path d="M13,5 L7,10 L13,15" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>
        <div ref={nextButtonRef} onClick={nextSlide} onMouseEnter={() => setIsHoveredNext(true)} onMouseLeave={() => setIsHoveredNext(false)} style={{ position: 'relative', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'transparent', borderRadius: '50%', cursor: 'pointer', margin: '0 5px', zIndex: 10 }}>
          <svg ref={nextSvgRef} width="40" height="40" viewBox="0 0 40 40" style={{ position: 'absolute', top: 0, left: 0, opacity: 0 }}>
            <circle cx="20" cy="20" r="18" fill="transparent" stroke="rgba(39, 68, 236, 0.767)" strokeWidth="1" className="next-button-border" />
          </svg>
          <div ref={nextIconRef} style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 2, opacity: 0 }}>
            <svg width="20" height="20" viewBox="0 0 20 20">
              <path d="M7,5 L13,10 L7,15" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>
      </div>
      
      {/* Updated slider container with aligned margins and consistent spacing */}
      <div className="custom-slider relative w-full ml-[2.5rem] mr-[2.5rem]" style={{ opacity: 0, transform: 'translateY(50px)' }} ref={sliderRef}>
        <Slider ref={slickSliderRef} {...settings}>
          {sliderHouses.map((house) => (
            <div key={house.id} className="pr-4">
              <div className="relative h-[350px] overflow-hidden rounded-[0.5rem] transform transition-all duration-300 ease-out hover:shadow-lg">
                <div 
                  className="image-bg absolute top-0 left-0 w-full h-full bg-cover bg-center transition-transform duration-500 hover:scale-105" 
                  style={{ backgroundImage: `url(${house.image})` }} 
                />
                <div className="absolute bottom-4 px-2 left-0 w-auto h-5 from-black/70 to-transparent p-4">
                  <h3 className="text-white text-lg bg-black px-2 rounded-md font-light">{house.country}</h3>
                </div>
              </div>
            </div>
          ))}
        </Slider>
      </div>
    </section>
  );
};

export default Services;